import { Box, Button, Grid, Typography } from '@mui/material'
import { useState, useEffect } from 'react'
import { CheckList } from '../../Components/CheckList'
import { SelectWithLabel, type SelectOption } from '../../Components/SelectWithLabel'
import { sendGet, sendPost, sendPostFormData } from '../../hooks/use-fetch'
import { DatePickerWithLabel } from '../../Components/DatePicker'
import { toast } from 'react-toastify'
import { EmailContentEnter } from './EmailContentEnter'
import { FormatDateValue } from '../../core/Utilities'
import { useAccountId } from '../../ContextProviders/CurrentAccount'
import { AccountType, type ActiveAccount } from '../../core/types'

export function FilterOptions (): JSX.Element {
  const [ageAsOfDate, setAgeAsOfDate] = useState<Date>(new Date())
  const [serviceProviders, setServiceProviders] = useState<Array<SelectOption<number>>>([])
  const [selectedServiceProviders, setSelectedServiceProviders] = useState<Array<SelectOption<number>>>([])
  const [programs, setPrograms] = useState<Array<SelectOption<number>>>([])
  const [selectedPrograms, setSelectedPrograms] = useState<Array<SelectOption<number>>>([])
  const [districts, setDistricts] = useState<Array<SelectOption<number>>>([])
  const [selectedDistricts, setSelectedDistricts] = useState<Array<SelectOption<number>>>([])
  const [enrollStatusOptions, setEnrollStatusOptions] = useState<Array<SelectOption<string>>>([])
  const [selectedEnrollStatus, setSelectedEnrollStatus] = useState<string>('All')
  const [selectedAgeOption, setSelectedAgeOption] = useState<Array<SelectOption<number>>>([])
  const [pageStage, setPageStage] = useState<number>(1)
  const [emailList, setEmailList] = useState<string[]>([])
  const [sendCount, setSentCount] = useState<number>(0)
  const accountId: ActiveAccount | undefined = useAccountId()

  const childAgeOptions = [{ id: 1, name: '1' }, { id: 2, name: '2' }, { id: 3, name: '3' }, { id: 4, name: '4' }, { id: 5, name: '5' }]

  const asOfDateChanged = (newValue: Date | null): void => {
    if (newValue != null) { setAgeAsOfDate(newValue) }
  }

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      const { response: response1 } = await sendGet('/CaregiverOutreach/EnrollmentStatuses', {})
      setEnrollStatusOptions(response1)
      const { response: response2 } = await sendGet('/CaregiverOutreach/ResidentDistricts', {})
      setDistricts(response2)
      const { response: response3 } = await sendGet('/CaregiverOutreach/ISDPrograms', {})
      setPrograms(response3)
      const { response: response4 } = await sendGet('/CaregiverOutreach/ServiceProviders', {})
      setServiceProviders(response4)
    }

    void fetchData()
  }, [])

  const programSelected = (arg: Array<SelectOption<number>>): void => {
    const anyOption = arg.find(s => s.id === -1)
    if (anyOption !== undefined) {
      setSelectedPrograms([anyOption])
    } else {
      setSelectedPrograms(arg.filter(s => s.id !== -1))
    }
  }

  const validateOptions = (): boolean => {
    let error = ''
    if (selectedAgeOption.length === 0) {
      error = 'Please select at least one age option.'
    } else if (selectedDistricts.length === 0 && accountId?.type !== AccountType.SERVICE_PROVIDER) {
      error = 'Please select at least one district.'
    } else if (selectedEnrollStatus !== 'All' && selectedPrograms.length === 0) {
      error = 'Please select at least one program.'
    } else if (selectedEnrollStatus !== 'All' && selectedServiceProviders.length === 0 && accountId?.type !== AccountType.SERVICE_PROVIDER) {
      error = 'Please select at least one service provider.'
    }

    if (error !== '') {
      toast.error(error)
      return false
    } else {
      return true
    }
  }

  const dataToSubmit = (): any => {
    const agesOfChild = selectedAgeOption.map(s => s.id)
    const residentDistrictIDs = selectedDistricts.map(s => s.id)
    const programIDs = selectedPrograms.map(s => s.id)
    const ServiceProviderIDs = selectedServiceProviders.map(s => s.id)

    return { agesOfChild, ageAsOfDate, enrollmentStatus: selectedEnrollStatus, residentDistrictIDs, programIDs, serviceProviderIDs: ServiceProviderIDs }
  }

  const openReport = async (): Promise<void> => {
    if (validateOptions()) {
      const postData = dataToSubmit()
      const { response, success } = await sendPost('/Reporting/ChildrenByAgeReportURL', postData)
      if (success) {
        window.open(response, '_blank')?.focus()
      }
    }
  }

  const emailDetails = async (): Promise<void> => {
    if (validateOptions()) {
      const postData = dataToSubmit()
      const { response, success } = await sendPost('/CaregiverOutreach/CaregiverEmails', postData)
      if (success) {
        setEmailList(response)
        setPageStage(2)
      }
    }
  }

  function serializeArray (fileData: FormData, name: string, array: number[]): void {
    for (let i = 0; i < array.length; i++) {
      fileData.append(`${name}[${i}]`, array[i].toString())
    }
  }

  const sendEmails = async (subject: string, body: string, attachment: File | null): Promise<void> => {
    if (confirm('Are you sure you want to send out emails to the listed addresses?')) {
      const fileData = new FormData()
      const filterOptions = dataToSubmit()
      serializeArray(fileData, 'agesOfChild', filterOptions.agesOfChild)
      fileData.append('ageAsOfDate', FormatDateValue(filterOptions.ageAsOfDate))
      fileData.append('enrollmentStatus', filterOptions.enrollmentStatus)
      serializeArray(fileData, 'residentDistrictIDs', filterOptions.residentDistrictIDs)
      serializeArray(fileData, 'programIDs', filterOptions.programIDs)
      serializeArray(fileData, 'serviceProviderIDs', filterOptions.serviceProviderIDs)
      fileData.append('subject', subject)
      fileData.append('body', body)
      if (attachment !== null) {
        fileData.append('attachment', attachment)
      }

      const { error, success, response } = await sendPostFormData('/CaregiverOutreach/SendEmails', fileData)
      if (success) {
        setSentCount(response)
        setPageStage(3)
      } else {
        toast.error(error)
      }
    }
  }

  return <>
    <Typography variant="h3">Caregiver Outreach</Typography>
    {pageStage === 1 &&
      <>
        <Grid container sx={{ display: 'flex', flexDirection: 'row' }} columnSpacing={1} rowSpacing={1}>
          <Grid item sm={6} md={3}>
            <CheckList label='Age of Child' items={childAgeOptions} selectedItems={selectedAgeOption} setSelectedItems={setSelectedAgeOption} allOption={false} dataTestId='childAge' />
          </Grid>
          <Grid item sm={12} md={6}>
            <DatePickerWithLabel
              required={true}
              label='Age As Of Date'
              name='ageAsOfDate'
              onChange={asOfDateChanged}
              value={ageAsOfDate}
            />
          </Grid>
        </Grid>
        <SelectWithLabel
          name="enrollStatus"
          label="Enrollment Status"
          data-testid='enrollStatus'
          width='250px'
          value={selectedEnrollStatus}
          options={enrollStatusOptions}
          onChange={(e: any) => { setSelectedEnrollStatus(e.target.value) }}
        />
        {selectedEnrollStatus !== 'All' && <Box data-testid='enrollmentFilterOptions'>
          <CheckList label='Programs Enrolled In' items={programs} selectedItems={selectedPrograms} setSelectedItems={programSelected} anyOption={true} dataTestId='programsEnrolledIn' />
          {accountId?.type !== AccountType.SERVICE_PROVIDER &&
            <span className='ml-10'>
              <CheckList label='Service Providers Enrolled In' items={serviceProviders} selectedItems={selectedServiceProviders} setSelectedItems={setSelectedServiceProviders} anyOption={true} dataTestId='providersEnrolledIn' />
            </span>
          }
        </Box>
        }
        {accountId?.type !== AccountType.SERVICE_PROVIDER && <CheckList label='Resident Districts' items={districts} selectedItems={selectedDistricts} setSelectedItems={setSelectedDistricts} allOption={true} dataTestId='residentDistrict' />}

        <Box className='mt-10'>
          <Button color='secondary' variant='contained' onClick={openReport}>Child List Report</Button>
          <Button color='secondary' variant='contained' className='ml-10' onClick={emailDetails} data-testid='emailCaregivers'>Email Caregivers</Button>
        </Box>
      </>
    }
    {pageStage === 2 &&
      <EmailContentEnter emailList={emailList} onBack={() => { setPageStage(1) }} onSendEmails={sendEmails} />
    }
    {pageStage === 3 &&
      <Typography variant='h6'>{sendCount} emails were sent.</Typography>
    }
  </>
}
